import { entries, partition, pipe, reduce } from 'remeda';

export type PanelStateValue = string;
export type PanelState = Record<string, PanelStateValue>;
export type AllStackPanelsState = Record<number, PanelState>;
export type AllStacksState = Record<number, AllStackPanelsState>;

export interface PanelLocation {
  /** Index of the stack */
  stack: number;
  /** Index of the panel */
  panel: number;
}

export const PANEL_STATE_PARAM_PREFIX_REGEX =
  /^(?<stack>[A-Z])(?<panel>\d{1,2})\.(?<key>.*)$/;

export function serializePanelStateParamKey(
  stack: number,
  panel: number,
  key: string,
) {
  const stackPrefix = String.fromCharCode(65 + stack);
  return `${stackPrefix}${panel}.${key}`;
}

export function parsePanelStateParamKey(param: string) {
  const { stack, panel, key } =
    PANEL_STATE_PARAM_PREFIX_REGEX.exec(param)!.groups!;
  return { stack: stack.charCodeAt(0) - 65, panel: parseInt(panel), key };
}

export function serializePanelStateToSearchParams(
  state: AllStacksState,
): Record<string, PanelStateValue> {
  const serializedState: Record<string, PanelStateValue> = {};
  for (const [stack, panelState] of entries(state)) {
    if (!panelState) {
      continue;
    }
    for (const [panel, state] of entries(panelState)) {
      if (!state) {
        continue;
      }
      for (const [key, value] of entries(state)) {
        serializedState[
          serializePanelStateParamKey(parseInt(stack), parseInt(panel), key)
        ] = value;
      }
    }
  }
  return serializedState;
}

export function deserializePanelStateFromSearchParams(
  search: object,
): [AllStacksState, object] {
  return pipe(
    search,
    entries(),
    partition((a) => PANEL_STATE_PARAM_PREFIX_REGEX.test(a[0])),
    ([panelStateParams, rest]) => [
      reduce((state, [kkey, value]) => {
        const { stack, panel, key } = parsePanelStateParamKey(kkey);
        const stackState = state[stack];
        if (!stackState) {
          state[stack] = { [panel]: { [key]: value } };
        } else {
          const panelState = stackState[panel];
          if (!panelState) {
            stackState[panel] = { [key]: value };
          } else {
            panelState[key] = value;
          }
        }
        return state;
      }, {} as AllStacksState)(panelStateParams),
      rest,
    ],
  );
}
